/**
 * Upgrade Modal Component
 * Beautiful modal for upselling modules/features
 */

import { X, Lock, Check } from 'lucide-react'
import { MODULE_METADATA, MODULE_B2B_PROJECTS } from '../constants/modules'

interface UpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  moduleName: string
  onRequestActivation?: () => void
}

function UpgradeModal({ isOpen, onClose, moduleName, onRequestActivation }: UpgradeModalProps) {
  if (!isOpen) return null

  const module = MODULE_METADATA[moduleName]
  if (!module) {
    console.warn(`Module metadata not found for: ${moduleName}`)
    // Fallback for B2B Projects module
    if (moduleName === MODULE_B2B_PROJECTS) {
      return (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose()
            }
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              B2B Projecten Module
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Beheer grotere bouwprojecten. Activeer de B2B-module voor appartementencomplexen, aannemersbeheer en bulk-generatie van keukens.
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  const handleRequestActivation = () => {
    if (onRequestActivation) {
      onRequestActivation()
    } else {
      // Default: open email or contact form
      window.location.href = `mailto:support@orion.local?subject=Activation Request: ${module.name}&body=Ik wil graag de ${module.name} module activeren voor mijn account.`
    }
    onClose()
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-t-lg">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center text-4xl">
              {module.icon}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {module.name}
              </h2>
              <p className="text-blue-100">
                Upgrade vereist voor deze module
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-4">
              {module.description}
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Vereiste tier:</strong> {module.tier_required}
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Wat krijg je met deze module?
            </h3>
            <ul className="space-y-2">
              {module.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {benefit}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Neem contact met ons op om deze module te activeren voor uw account.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleRequestActivation}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <Lock className="w-5 h-5" />
                Vraag Activatie Aan
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpgradeModal
